import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TicketDto} from "../../../model/dto/entity/ticket.dto";
import {UiTicketQuickViewDto} from "./data/ui-ticket-quick-view.dto";
import {TicketObjectDto} from "../../../model/dto/entity/ticket-object.dto";
import {TicketService} from "../../../service/api/ticket.service";
import {ObjectRequestList} from "../../../../../core/shared/dto/object/request/object-request-list";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectRequestComplex} from "../../../../../core/shared/dto/object/request/object-request-complex";
import {ObjectRequestComplexNode} from "../../../../../core/shared/dto/object/request/object-request-complex-node";
import {ObjectRequestRelation} from "../../../../../core/shared/dto/object/request/object-request-relation";
import {DtoDetail} from "../../../../../core/shared/dto/dto.detail";
import {QlService} from "../../../../../core/shared/ql/service/ql.service";

export type TicketObjectInlineComponentSize = 'XL' | 'L' | 'M' | 'S' | 'XS'

@Component({
    selector: 'ticket-object-inline',
    templateUrl: './ticket-object-inline.component.html',
    standalone: false
})
export class TicketObjectInlineComponent implements OnInit, OnChanges {

    initialized: boolean;
    busy: boolean;

    @Input() dto: TicketDto;
    @Input() size: TicketObjectInlineComponentSize = 'M';
    @Input() viewType: TicketObjectInlineComponentSize;
    @Input() ticketId: number;
    uiTicketDto: UiTicketQuickViewDto;

    constructor(
        protected ticketService: TicketService,
        protected qlService: QlService) {
    }

    ngOnInit() {

        if (this.viewType) {
            this.size = this.viewType;
        }

        this.refreshComponent();
    }


    protected refreshComponent() {
        if (this.dto) {
            this.postProcessDto(this.dto);
            return;
        }

        this.busy = true;

        const objectRequestList = new ObjectRequestList(
            true,
            [FilterCriteria.create('id', FilterCriteria.cOperatorEqual, this.ticketId)],
            []
        );
        const nodes: ObjectRequestComplexNode[] = [
            ObjectRequestComplexNode.createRelationNode(
                'ticketObjects',
                ObjectRequestRelation.createJoin('tm.TicketObject#ticket')
            )
        ];

        // TM-1000002018: the pin popover (XS) shows the linked customer's name and the linked contract's VSNR.
        // Both come from the backend's virtual joins on tm.Ticket / cm.Contract in this ONE request — no extra
        // calls and no cross-feature services (BOUND-006). Other sizes keep the request unchanged.
        if (this.size == 'XS') {
            nodes.push(
                ObjectRequestComplexNode.createRelationNode(
                    'objectLatestCustomer',
                    ObjectRequestRelation.createJoin('+objectLatestCustomer')
                ),
                ObjectRequestComplexNode.createRelationNode(
                    'objectLatestContract',
                    ObjectRequestRelation.createJoin('+objectLatestContract')
                ).addNodes(
                    ObjectRequestComplexNode.createRelationNode(
                        'currentMainCustomer',
                        ObjectRequestRelation.createJoin('+currentMainCustomer')
                    )
                )
            );
        }

        objectRequestList.objectRequestComplex = ObjectRequestComplex.build(true, false, ...nodes);

        this.ticketService.list(objectRequestList).subscribe(res => {
            const dto: TicketDto = res.entries[0];
            this.dto = dto;
            dto.form = res.form;
            this.postProcessDto(dto);
        });
    }

    postProcessDto(dto: DtoDetail) {
        this.uiTicketDto = new UiTicketQuickViewDto();
        this.uiTicketDto.name = dto.name;
        this.uiTicketDto.type = dto['typeDtoName'];
        this.uiTicketDto.alias = dto['alias'];
        this.uiTicketDto.uiObjectsLength = dto['ticketObjects'] ? dto['ticketObjects'].length : 0;

        // TM-1000002018: human-readable object line instead of the raw object id
        this.uiTicketDto.uiObjectLabel = this.buildObjectLabel(dto);

        if (this.uiTicketDto.uiObjectsLength) {

            const customerObject = this.objectExists('Customer', dto['ticketObjects']);
            const contractObject = this.objectExists('Contract', dto['ticketObjects']);

            // TM-1000002018: „+N weitere" counts only the objects the label does not already name
            // (the linked customer and the linked contract count one each); never negative
            const representedCount = (customerObject ? 1 : 0) + (contractObject ? 1 : 0);
            this.uiTicketDto.uiExtraObjectsLength = Math.max(0, this.uiTicketDto.uiObjectsLength - representedCount);

            if (customerObject) {
                this.uiTicketDto.uiIcon = 'fa-regular fa-user'; // prefer customer
                this.uiTicketDto.uiBgColor = 'bg-primary-100 text-primary';
            } else if (contractObject) {
                this.uiTicketDto.uiIcon = 'fa-regular fa-file';
                this.uiTicketDto.uiBgColor = 'bg-blue-100 text-primary border-round';
            }

        } else {
            this.uiTicketDto.uiIcon = 'fa-regular fa-ticket'; // just show ticket icon
            this.uiTicketDto.uiBgColor = 'bg-yellow-100 text-yellow-600 border-round';
        }
        this.busy = false;
        this.initialized = true;

    }

    /**
     * TM-1000002018: builds the pinned-ticket object line from the joined DTOs.
     * Read via bracket access on purpose — CustomerDto/ContractDto live in feature-crm (BOUND-006).
     *
     * Rules: „name (VSNR: x)" · „name" · „VSNR: x" (contract without main customer) · empty.
     * The name is the linked customer's; without one, the linked contract's current main customer.
     */
    private buildObjectLabel(dto: DtoDetail): string {
        const customerName: string = dto['objectLatestCustomer']?.personDtoName
            ?? dto['objectLatestContract']?.currentMainCustomer?.personDtoName;
        const vsn: string = dto['objectLatestContract']?.contractIdentifier;

        if (customerName && vsn) {
            return `${customerName} (VSNR: ${vsn})`;
        }
        if (customerName) {
            return customerName;
        }
        if (vsn) {
            return `VSNR: ${vsn}`;
        }
        return '';
    }

    objectExists(objectType: string, ticketObjects: TicketObjectDto[]): TicketObjectDto {
        return ticketObjects.find(item => item.objectTypeDtoName == objectType);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (!this.initialized) {
            return;
        }

        this.refreshComponent();

    }

}
